const STL_HEADER_BYTES = 84; // 80-byte header + 4-byte face count
const STL_FACE_RECORD_SIZE = 50; // 12 floats (normal + 3 vertices) + 2 byte attribute
const SOLID_BYTES = [115, 111, 108, 105, 100]; // "solid" in ASCII

/**
 * Validate that an ArrayBuffer looks like real STL data (binary or ASCII).
 *
 * Mirrors the heuristic Three.js uses in STLLoader.isBinary():
 *   1. Binary STL: 80-byte header + 4-byte uint32 face count + N×50 bytes.
 *      If the total size exactly matches, accept.
 *   2. ASCII STL: must contain "solid" within the first 5 bytes (allowing
 *      for a BOM prefix up to 5 bytes long).
 *   3. Anything else (HTML error pages, STEP files, etc.) is rejected
 *      before Three.js can attempt a multi-GB typed-array allocation.
 */
export function validateStlBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < STL_HEADER_BYTES) return false;

  const view = new DataView(buffer);
  const faceCount = view.getUint32(80, true);
  const expectedSize = STL_HEADER_BYTES + faceCount * STL_FACE_RECORD_SIZE;

  if (expectedSize === buffer.byteLength) return true;

  for (let off = 0; off < 5; off++) {
    let match = true;
    for (let i = 0; i < SOLID_BYTES.length; i++) {
      if (view.getUint8(off + i) !== SOLID_BYTES[i]) {
        match = false;
        break;
      }
    }
    if (match) return true;
  }

  return false;
}

const GLB_MAGIC = 0x46546c67; // "glTF" read as a little-endian uint32
const GLB_HEADER_BYTES = 12;
const GLB_CHUNK_HEADER_BYTES = 8;
const GLB_MIN_BYTES = GLB_HEADER_BYTES + GLB_CHUNK_HEADER_BYTES;
const UTF8_BOM = [0xef, 0xbb, 0xbf];
const JSON_WHITESPACE = new Set([0x20, 0x09, 0x0a, 0x0d]);
const OPEN_BRACE = 0x7b;

// GLTFLoader trusts every chunk length it reads, so a chunk running past the
// file fails with the same RangeError as an empty file.
function isCompleteGlb(view: DataView): boolean {
  if (view.byteLength < GLB_HEADER_BYTES) return false;
  const declaredLength = view.getUint32(8, true);
  if (declaredLength < GLB_MIN_BYTES || declaredLength > view.byteLength) return false;
  let offset = GLB_HEADER_BYTES;
  while (declaredLength - offset >= GLB_CHUNK_HEADER_BYTES) {
    offset += GLB_CHUNK_HEADER_BYTES + view.getUint32(offset, true);
  }
  return offset === declaredLength;
}

function startsLikeJsonObject(bytes: Uint8Array): boolean {
  let i = UTF8_BOM.every((b, k) => bytes[k] === b) ? UTF8_BOM.length : 0;
  while (i < bytes.length && JSON_WHITESPACE.has(bytes[i])) i++;
  return bytes[i] === OPEN_BRACE;
}

/**
 * Whether GLTFLoader can safely be handed this buffer: a binary glTF whose
 * chunks all fit inside its declared length, or a JSON glTF. GLTFLoader
 * throws a RangeError on anything under 4 bytes before it gets as far as its
 * own error handling.
 */
export function validateGltfBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const view = new DataView(buffer);
  if (view.getUint32(0, true) === GLB_MAGIC) return isCompleteGlb(view);
  return startsLikeJsonObject(new Uint8Array(buffer));
}
